/**
 * Учёт доступности сервера для офлайн-режима.
 *
 * Раньше приложение показывало полноэкранную заглушку после ЛЮБОГО
 * одиночного сбоя запроса: один таймаут — и весь интерфейс исчезал,
 * хотя данные уже были загружены и лежали в памяти. Здесь копим сбои
 * подряд и объявляем сервер недоступным только после
 * OFFLINE_FAILURE_THRESHOLD подряд идущих сбоев. Любой ответ сервера
 * (в том числе 4xx) сбрасывает счётчик.
 */

/** Сколько сбоев подряд считаем «сервер недоступен». */
export const OFFLINE_FAILURE_THRESHOLD = 3;

let consecutiveFailures = 0;
const listeners = new Set<() => void>();

const emit = (): void => {
    listeners.forEach((listener) => listener());
};

/**
 * У запроса не было ответа вовсе: нет сети, таймаут, обрыв соединения.
 * Именно такой сбой service worker умеет отложить в очередь повторной
 * отправки. Отменённый запрос (axios ERR_CANCELED) сбоем не считаем.
 */
export const isNoResponseFailure = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') return false;
    const candidate = error as { code?: unknown; response?: unknown };
    if (candidate.code === 'ERR_CANCELED') return false;
    return !candidate.response;
};

/**
 * Сбой означает, что ответа от сервера не было вовсе (нет сети,
 * таймаут, обрыв) либо шлюз вернул 5xx — то есть сервер не работает.
 * Ответы 4xx (400/401/403/404/422…) означают, что сервер жив.
 */
export const isServerUnreachable = (error: unknown): boolean => {
    if (isNoResponseFailure(error)) return true;
    if (!error || typeof error !== 'object') return false;
    const status = (error as { response?: { status?: unknown } }).response?.status;
    return typeof status === 'number' && status >= 500;
};

/** Сервер считается недоступным после N сбоев подряд. */
export const isServerUnavailable = (): boolean =>
    consecutiveFailures >= OFFLINE_FAILURE_THRESHOLD;

/** Сколько сбоев накопилось подряд — для тестов и отладки. */
export const getConsecutiveFailures = (): number => consecutiveFailures;

/** Ответ сервера получен: счётчик сбоев сбрасывается. */
export const reportNetworkSuccess = (): void => {
    if (consecutiveFailures === 0) return;
    consecutiveFailures = 0;
    emit();
};

/** Запрос не получил внятного ответа: сбой копим. */
export const reportNetworkFailure = (): void => {
    consecutiveFailures += 1;
    emit();
};

/** Сбросить счётчик: кнопка «Обновить», возврат в приложение, тесты. */
export const resetNetworkStatus = (): void => {
    if (consecutiveFailures === 0) return;
    consecutiveFailures = 0;
    emit();
};

/** Подписка на изменения статуса; возвращает функцию отписки. */
export const subscribeNetworkStatus = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};
